// TOGAF ADM phases + bilingual phase briefings + studio output catalog.

export interface AdmPhase {
  id: string;
  name: string;
  nameJa: string;
  color: string;
  icon: string;
}

export const ADM_PHASES: AdmPhase[] = [
  { id: 'preliminary', name: 'Preliminary', nameJa: '準備フェーズ', color: '#64748b', icon: '🏗️' },
  { id: 'vision', name: 'Phase A: Architecture Vision', nameJa: 'フェーズA: アーキテクチャビジョン', color: '#3b82f6', icon: '👁️' },
  { id: 'business', name: 'Phase B: Business Architecture', nameJa: 'フェーズB: ビジネスアーキテクチャ', color: '#10b981', icon: '💼' },
  { id: 'information', name: 'Phase C: Information Systems', nameJa: 'フェーズC: 情報システム', color: '#8b5cf6', icon: '💾' },
  { id: 'technology', name: 'Phase D: Technology Architecture', nameJa: 'フェーズD: テクノロジーアーキテクチャ', color: '#f59e0b', icon: '⚙️' },
  { id: 'opportunities', name: 'Phase E: Opportunities & Solutions', nameJa: 'フェーズE: 機会とソリューション', color: '#ec4899', icon: '💡' },
  { id: 'migration', name: 'Phase F: Migration Planning', nameJa: 'フェーズF: 移行計画', color: '#14b8a6', icon: '🚀' },
  { id: 'governance', name: 'Phase G: Implementation Governance', nameJa: 'フェーズG: 実装ガバナンス', color: '#ef4444', icon: '🏛️' },
  { id: 'change', name: 'Phase H: Architecture Change Management', nameJa: 'フェーズH: 変更管理', color: '#6366f1', icon: '🔄' },
];

export interface PhaseDetail {
  purpose: { ja: string; en: string };
  tasks: { ja: string[]; en: string[] };
  who: { ja: string[]; en: string[] };
  deliverables: { ja: string[]; en: string[] };
}

export const TOGAF_ADM_DETAILS: { [key: string]: PhaseDetail } = {
  preliminary: {
    purpose: {
      ja: 'アーキテクチャ能力の準備と原則の確立',
      en: 'Prepare the architecture capability and establish principles',
    },
    tasks: {
      ja: ['組織のコンテキスト定義', 'アーキテクチャ原則の策定', 'ツールと方法論の選定', 'ガバナンスフレームワークの確立'],
      en: ['Define organizational context', 'Establish architecture principles', 'Select tools and methodology', 'Establish governance framework'],
    },
    who: {
      ja: ['エンタープライズアーキテクト', 'CIO/CTO', 'ガバナンス委員会'],
      en: ['Enterprise Architect', 'CIO/CTO', 'Governance Board'],
    },
    deliverables: {
      ja: ['アーキテクチャ原則書', '組織モデル', 'ツール・技法選定書', 'ガバナンスフレームワーク'],
      en: ['Architecture Principles', 'Organizational Model', 'Tool & Technique Selection', 'Governance Framework'],
    },
  },
  vision: {
    purpose: {
      ja: '目的、背景、スコープ、関係者整理',
      en: 'Clarify goals, context, scope and stakeholders',
    },
    tasks: {
      ja: ['ステークホルダーの特定と懸念事項の整理', 'ビジネス目標の明確化', 'スコープの定義', 'アーキテクチャビジョンの策定', '承認の取得'],
      en: ['Identify stakeholders and concerns', 'Clarify business goals', 'Define scope', 'Develop the Architecture Vision', 'Obtain approval'],
    },
    who: {
      ja: ['エンタープライズアーキテクト', 'ビジネススポンサー', 'プロジェクトマネージャー', 'ステークホルダー代表'],
      en: ['Enterprise Architect', 'Business Sponsor', 'Project Manager', 'Stakeholder Representatives'],
    },
    deliverables: {
      ja: ['アーキテクチャビジョン文書', 'ステークホルダーマップ', 'ビジネス目標定義書', 'スコープ定義書', '承認済みプロジェクト憲章'],
      en: ['Architecture Vision', 'Stakeholder Map', 'Business Goals Definition', 'Scope Definition', 'Approved Statement of Architecture Work'],
    },
  },
  business: {
    purpose: {
      ja: '業務プロセス、Capability定義',
      en: 'Define business processes and capabilities',
    },
    tasks: {
      ja: ['現状業務アーキテクチャ(As-Is)の分析', '目標業務アーキテクチャ(To-Be)の設計', '業務プロセスのモデリング', 'ビジネスCapabilityの定義', 'ギャップ分析'],
      en: ['Analyze baseline (As-Is) business architecture', 'Design target (To-Be) business architecture', 'Model business processes', 'Define business capabilities', 'Gap analysis'],
    },
    who: {
      ja: ['ビジネスアーキテクト', '業務部門リーダー', 'プロセスオーナー', 'ビジネスアナリスト'],
      en: ['Business Architect', 'Business Unit Leaders', 'Process Owners', 'Business Analysts'],
    },
    deliverables: {
      ja: ['業務アーキテクチャ文書', 'ビジネスプロセスモデル', 'Capabilityマップ', '組織構造図', 'ギャップ分析レポート'],
      en: ['Business Architecture Document', 'Business Process Models', 'Capability Map', 'Organization Structure', 'Gap Analysis Report'],
    },
  },
  information: {
    purpose: {
      ja: 'データモデル、機能構成、インターフェース',
      en: 'Data models, application functions and interfaces',
    },
    tasks: {
      ja: ['データアーキテクチャの設計', 'アプリケーション機能の定義', 'インターフェース設計', 'データフロー分析', 'システム間連携の設計'],
      en: ['Design data architecture', 'Define application functions', 'Design interfaces', 'Analyze data flows', 'Design system integrations'],
    },
    who: {
      ja: ['データアーキテクト', 'アプリケーションアーキテクト', 'システムアナリスト', 'DBエンジニア'],
      en: ['Data Architect', 'Application Architect', 'System Analysts', 'DB Engineers'],
    },
    deliverables: {
      ja: ['データモデル', 'アプリケーション機能一覧', 'インターフェース仕様書', 'データフロー図', 'システム連携図'],
      en: ['Data Models', 'Application Function Catalog', 'Interface Specifications', 'Data Flow Diagrams', 'Integration Diagrams'],
    },
  },
  technology: {
    purpose: {
      ja: 'インフラ構成、セキュリティ方針',
      en: 'Infrastructure landscape and security policy',
    },
    tasks: {
      ja: ['技術スタックの選定', 'インフラストラクチャ設計', 'セキュリティアーキテクチャの策定', 'ネットワーク設計', '非機能要件の定義'],
      en: ['Select technology stack', 'Design infrastructure', 'Define security architecture', 'Network design', 'Define non-functional requirements'],
    },
    who: {
      ja: ['インフラアーキテクト', 'セキュリティアーキテクト', 'ネットワークエンジニア', 'クラウドアーキテクト'],
      en: ['Infrastructure Architect', 'Security Architect', 'Network Engineers', 'Cloud Architects'],
    },
    deliverables: {
      ja: ['技術アーキテクチャ文書', 'インフラ構成図', 'セキュリティ方針書', 'ネットワーク設計書', '非機能要件定義書'],
      en: ['Technology Architecture Document', 'Infrastructure Diagrams', 'Security Policy', 'Network Design', 'NFR Definition'],
    },
  },
  opportunities: {
    purpose: {
      ja: '実装プロジェクトの特定、移行アーキテクチャの定義',
      en: 'Identify implementation projects and transition architectures',
    },
    tasks: {
      ja: ['実装オプションの評価', 'ソリューションの特定', '移行アーキテクチャの定義', 'ギャップ分析結果の統合', 'コスト・ベネフィット分析'],
      en: ['Evaluate implementation options', 'Identify solutions', 'Define transition architectures', 'Consolidate gap analysis', 'Cost-benefit analysis'],
    },
    who: {
      ja: ['ソリューションアーキテクト', 'プロジェクトマネージャー', 'ビジネスアナリスト', '調達担当'],
      en: ['Solution Architect', 'Project Manager', 'Business Analysts', 'Procurement'],
    },
    deliverables: {
      ja: ['ギャップ分析結果', '統合ロードマップ', 'コスト・ベネフィット分析', 'ソリューション評価書', '移行アーキテクチャ定義書'],
      en: ['Consolidated Gap Analysis', 'Integrated Roadmap', 'Cost-Benefit Analysis', 'Solution Assessment', 'Transition Architecture Definition'],
    },
  },
  migration: {
    purpose: {
      ja: '詳細な実装・移行計画の策定、優先順位付け',
      en: 'Detailed implementation/migration planning and prioritization',
    },
    tasks: {
      ja: ['詳細移行計画の策定', '実装優先順位の決定', 'リスク評価と対策', 'リソース計画', '移行スケジュールの作成'],
      en: ['Develop detailed migration plan', 'Prioritize implementation', 'Assess and mitigate risks', 'Resource planning', 'Build migration schedule'],
    },
    who: {
      ja: ['プロジェクトマネージャー', '移行リード', 'リスクマネージャー', 'リソースマネージャー'],
      en: ['Project Manager', 'Migration Lead', 'Risk Manager', 'Resource Manager'],
    },
    deliverables: {
      ja: ['詳細移行計画書', '実装優先順位マトリクス', 'リスク評価・対策書', 'リソース計画書', '移行スケジュール'],
      en: ['Detailed Migration Plan', 'Implementation Priority Matrix', 'Risk Assessment & Mitigation', 'Resource Plan', 'Migration Schedule'],
    },
  },
  governance: {
    purpose: {
      ja: '実装プロジェクトの監督、アーキテクチャ準拠の確認',
      en: 'Oversee implementation and ensure architecture compliance',
    },
    tasks: {
      ja: ['実装プロジェクトの監視', 'アーキテクチャ準拠の確認', '変更要求の管理', '実装ガイドラインの提供', 'レビューの実施'],
      en: ['Monitor implementation projects', 'Verify architecture compliance', 'Manage change requests', 'Provide implementation guidance', 'Conduct reviews'],
    },
    who: {
      ja: ['ガバナンス委員会', 'プロジェクト監査', 'アーキテクチャレビューボード', 'QAチーム'],
      en: ['Governance Board', 'Project Audit', 'Architecture Review Board', 'QA Team'],
    },
    deliverables: {
      ja: ['実装プロジェクト監視レポート', 'アーキテクチャ準拠チェックリスト', '変更要求管理台帳', '実装ガイドライン', 'レビュー記録'],
      en: ['Implementation Monitoring Reports', 'Compliance Checklists', 'Change Request Register', 'Implementation Guidelines', 'Review Records'],
    },
  },
  change: {
    purpose: {
      ja: '変更要求の管理、アーキテクチャの継続的な改善',
      en: 'Manage change requests and continuously improve the architecture',
    },
    tasks: {
      ja: ['変更要求の評価', 'アーキテクチャの更新', '教訓の記録', '成熟度評価', '次サイクルの計画'],
      en: ['Evaluate change requests', 'Update the architecture', 'Record lessons learned', 'Assess maturity', 'Plan the next cycle'],
    },
    who: {
      ja: ['変更管理委員会', 'エンタープライズアーキテクト', '品質保証チーム', '継続改善チーム'],
      en: ['Change Advisory Board', 'Enterprise Architect', 'QA Team', 'Continuous Improvement Team'],
    },
    deliverables: {
      ja: ['変更要求評価書', 'アーキテクチャ更新履歴', '教訓・ベストプラクティス', 'アーキテクチャ成熟度評価', '次サイクル計画'],
      en: ['Change Request Assessments', 'Architecture Update History', 'Lessons Learned', 'Architecture Maturity Assessment', 'Next Cycle Plan'],
    },
  },
};

export interface StudioOutputDef {
  id: string;
  name: string;
  nameJa: string;
  icon: string;
}

export const STUDIO_OUTPUTS: StudioOutputDef[] = [
  { id: 'summary', name: 'Summary', nameJa: 'サマリー', icon: '📝' },
  { id: 'mindmap', name: 'Mind Map', nameJa: 'マインドマップ', icon: '🗺️' },
  { id: 'deliverables', name: 'Deliverables', nameJa: '成果物', icon: '📦' },
  { id: 'artifacts', name: 'Artifacts', nameJa: 'アーティファクト', icon: '📋' },
  { id: 'risks', name: 'Risk Analysis', nameJa: 'リスク分析', icon: '⚠️' },
  { id: 'roadmap', name: 'Roadmap', nameJa: 'ロードマップ', icon: '🛤️' },
  { id: 'gaps', name: 'Gap Analysis', nameJa: 'ギャップ分析', icon: '📊' },
  { id: 'recommendations', name: 'Recommendations', nameJa: '推奨事項', icon: '✅' },
];
